import React, { useState, useMemo, useEffect } from "react";
import * as XLSX from "xlsx";
import axiosInstance from "../utils/axios";
import {
  DB_FIELDS,
  detectHeaders,
  guessMapping,
} from "../constants/importMappingConfig";
import { fetchAllCommunities } from "../api/superAdminApi";
import { fetchSubdivisions } from "../api/subdivisionApi";

const propertyTypes = [
  { value: "Sale", label: "For Sale" },
  { value: "Rent", label: "For Rent" },
  { value: "Sold", label: "Sold" },
  { value: "Rented", label: "Rented" },
];

const DROPDOWN_VALUE_ALIASES = {
  private_pool: { yes: "Yes", no: "No", unk: "Unknown", ukn: "Unknown", unknown: "Unknown" },
  community_pool: { yes: "Yes", no: "No", unk: "Unknown", ukn: "Unknown", unknown: "Unknown" },
  water_view: { yes: "YES", no: "NO", unk: "UKN", ukn: "UKN", unknown: "UKN" },
  furniture: {
    furnished: "Furnished",
    fully: "Furnished",
    unfurnished: "Unfurnished",
    none: "Unfurnished",
    part: "Partially",
    partially: "Partially",
    negotiable: "Negotiable",
    unk: "Unknown",
    ukn: "Unknown",
    unknown: "Unknown",
  },
  pets: { yes: "Yes", no: "No", unk: "UKN", ukn: "UKN", unknown: "UKN" },
  garage: { unk: "Unknown", ukn: "Unknown", unknown: "Unknown" },
  rcr_seller: { y: "YES", yes: "YES", true: "YES", 1: "YES", n: "NO", no: "NO", false: "NO", 0: "NO", unk: "UKN", ukn: "UKN", unknown: "UKN" },
  rcr_landlord: { y: "YES", yes: "YES", true: "YES", 1: "YES", n: "NO", no: "NO", false: "NO", 0: "NO", unk: "UKN", ukn: "UKN", unknown: "UKN" },
  rcr_buyer: { y: "YES", yes: "YES", true: "YES", 1: "YES", n: "NO", no: "NO", false: "NO", 0: "NO", unk: "UKN", ukn: "UKN", unknown: "UKN" },
  rcr_tenant: { y: "YES", yes: "YES", true: "YES", 1: "YES", n: "NO", no: "NO", false: "NO", 0: "NO", unk: "UKN", ukn: "UKN", unknown: "UKN" },
};

function getOptionValue(option) {
  return typeof option === "object" ? option.value : option;
}

function normalizeDropdownValue(fieldKey, value) {
  if (value === null || value === undefined || value === "") return value;
  const normalized = String(value).trim();
  const alias = DROPDOWN_VALUE_ALIASES[fieldKey]?.[normalized.toLowerCase()];
  return alias || normalized;
}

function matchDropdownValue(fieldKey, value, options) {
  if (!value || !options) return value;
  const normalized = normalizeDropdownValue(fieldKey, value);

  for (const opt of options) {
    const optVal = getOptionValue(opt);
    if (optVal === normalized) return optVal;
  }

  const lower = String(normalized).toLowerCase();
  for (const opt of options) {
    const optVal = getOptionValue(opt);
    if (optVal.toLowerCase() === lower) return optVal;
  }

  if (fieldKey === "garage") {
    for (const opt of options) {
      const optVal = getOptionValue(opt);
      if (optVal.toLowerCase().startsWith(lower + "-")) return optVal;
    }
  }

  return normalized;
}

function excelDateToString(serial) {
  if (typeof serial !== "number") return serial;
  const utc_days = Math.floor(serial - 25569);
  const utc_value = utc_days * 86400;
  const date_info = new Date(utc_value * 1000);
  const day = String(date_info.getUTCDate()).padStart(2, "0");
  const month = String(date_info.getUTCMonth() + 1).padStart(2, "0");
  const year = date_info.getUTCFullYear();
  return `${day}.${month}.${year}`;
}

/**
 * Inline column mapping UI (step 2).
 * Shows detected Excel columns and lets the user confirm/change
 * which DB field each one maps to.
 */
function ColumnMappingStep({
  dynamicDBFields,
  excelHeaders,
  allRows,
  mapping,
  onMappingChange,
  onConfirm,
  onBack,
  rowCount,
}) {
  const [editableRows, setEditableRows] = useState(() => {
    // Deep copy and normalize dropdown values to match option casing
    const dbFieldMapLocal = {};
    for (const f of dynamicDBFields) dbFieldMapLocal[f.key] = f;

    return allRows.map((row) => {
      const newRow = Array.isArray(row) ? [...row] : { ...row };
      // Normalize dropdown columns
      for (let i = 0; i < excelHeaders.length; i++) {
        const dbField = mapping[i];
        if (!dbField) continue;
        const fieldDef = dbFieldMapLocal[dbField];
        if (fieldDef?.inputType === "select" && fieldDef?.options) {
          const val = Array.isArray(newRow) ? newRow[i] : newRow[i];
          if (val !== null && val !== undefined && val !== "") {
            const matched = matchDropdownValue(dbField, val, fieldDef.options);
            if (fieldDef.options.some((opt) => getOptionValue(opt) === matched)) {
              if (Array.isArray(newRow)) {
                newRow[i] = matched;
              } else {
                newRow[i] = matched;
              }
            }
          }
        }
      }
      return newRow;
    });
  });

  const usedFields = useMemo(() => {
    const set = new Set();
    for (const val of Object.values(mapping)) {
      if (val) set.add(val);
    }
    return set;
  }, [mapping]);

  const dbFieldMap = useMemo(() => {
    const m = {};
    for (const f of dynamicDBFields) m[f.key] = f;
    return m;
  }, [dynamicDBFields]);

  const mappedEntries = useMemo(() => {
    return excelHeaders
      .map((h, i) => ({ header: h, index: i, dbField: mapping[i] }))
      .filter((e) => e.dbField);
  }, [excelHeaders, mapping]);

  const mappedCount = Object.values(mapping).filter(Boolean).length;

  // Detect if any dropdown cell in the preview is currently using an unmatched value
  const hasUnmatchedDropdown = useMemo(() => {
    if (!mappedEntries.length || !editableRows.length) return false;
    for (let rIdx = 0; rIdx < editableRows.length; rIdx++) {
      const row = editableRows[rIdx];
      for (const e of mappedEntries) {
        const fieldDef = dbFieldMap[e.dbField];
        const isDropdown = fieldDef?.inputType === "select" && fieldDef?.options;
        if (!isDropdown) continue;
        const val = Array.isArray(row) ? row[e.index] : row[e.index];
        const rawVal = val !== null && val !== undefined ? String(val) : "";
        if (!rawVal) continue;
        const strVal = matchDropdownValue(e.dbField, rawVal, fieldDef.options);
        const isUnmatched =
          strVal &&
          !fieldDef.options.some(
            (opt) => getOptionValue(opt) === strVal
          );
        if (isUnmatched) return true;
      }
    }
    return false;
  }, [editableRows, mappedEntries, dbFieldMap]);

  const handleEditCell = (rowIdx, colIdx, newValue) => {
    setEditableRows((prev) => {
      const copy = [...prev];
      if (Array.isArray(copy[rowIdx])) {
        copy[rowIdx] = [...copy[rowIdx]];
        copy[rowIdx][colIdx] = newValue;
      } else {
        copy[rowIdx] = { ...copy[rowIdx], [colIdx]: newValue };
      }
      return copy;
    });
  };

  const handleConfirmClick = () => {
    // If there are skipped columns that still contain data, ask for explicit confirmation
    const skippedWithData = [];
    for (let idx = 0; idx < excelHeaders.length; idx++) {
      const dbField = mapping[idx];
      if (dbField) continue;

      const hasData = editableRows.some((row) => {
        const val = Array.isArray(row) ? row[idx] : row[idx];
        return val !== "" && val !== null && val !== undefined;
      });
      if (hasData) {
        skippedWithData.push(excelHeaders[idx] || `(column #${idx + 1})`);
      }
    }

    if (skippedWithData.length > 0) {
      const msg = [
        "There are Excel columns with data that are currently skipped (not mapped):",
        "",
        skippedWithData.slice(0, 10).join(", "),
        skippedWithData.length > 10
          ? `…and ${skippedWithData.length - 10} more.`
          : "",
        "",
        "These values will NOT be imported. Do you still want to continue?",
      ]
        .filter(Boolean)
        .join("\n");

      // If user cancels, abort confirm
      // eslint-disable-next-line no-alert
      const ok = window.confirm(msg);
      if (!ok) return;
    }

    onConfirm(editableRows);
  };

  const sampleRow = editableRows[0] || [];

  return (
    <div>
      <h2 className="text-xl font-bold mb-2">
        Map Excel columns → database fields
      </h2>
      <p className="text-sm text-gray-500 mb-4">
        {mappedCount} of {excelHeaders.length} columns mapped &middot;{" "}
        {rowCount} data rows detected
      </p>
      <div
        style={{
          maxHeight: "40vh",
          overflowY: "auto",
          border: "1px solid #e5e7eb",
          borderRadius: "8px",
        }}
      >
        <table
          style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}
        >
          <thead>
            <tr style={{ background: "#f8fafc", position: "sticky", top: 0, zIndex: 5 }}>
              <th style={thStyle}>#</th>
              <th style={thStyle}>Excel Column</th>
              <th style={thStyle}>Sample Value</th>
              <th style={thStyle}>→</th>
              <th style={thStyle}>Database Field</th>
            </tr>
          </thead>
          <tbody>
            {excelHeaders.map((header, idx) => {
              const currentField = mapping[idx];
              const sample = sampleRow ? sampleRow[idx] : "";
              const fieldDef = currentField ? dbFieldMap[currentField] : null;
              return (
                <tr
                  key={idx}
                  style={{
                    background: currentField ? "#f0fdf4" : "#fefce8",
                    borderBottom: "1px solid #e5e7eb",
                  }}
                >
                  <td style={tdStyle}>{idx + 1}</td>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>
                    {header || "(empty)"}
                  </td>
                  <td style={tdStyle}>
                    <span
                      style={{
                        fontFamily: "monospace",
                        fontSize: "12px",
                        color: "#64748b",
                        background: "rgba(0,0,0,.04)",
                        padding: "2px 6px",
                        borderRadius: "4px",
                      }}
                    >
                      {sample !== "" && sample != null
                        ? String(sample).substring(0, 40)
                        : "—"}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, textAlign: "center", fontWeight: 700 }}>
                    →
                  </td>
                  <td style={tdStyle}>
                    <select
                      value={currentField || ""}
                      onChange={(e) =>
                        onMappingChange(idx, e.target.value || null)
                      }
                      style={{
                        width: "100%",
                        padding: "4px 8px",
                        border: `1px solid ${currentField ? "#22c55e" : "#e5e7eb"}`,
                        borderRadius: "6px",
                        fontSize: "13px",
                        background: currentField ? "#f0fdf4" : "#fff",
                      }}
                    >
                      <option value="">— Skip —</option>
                      {dynamicDBFields.map((f) => {
                        const isUsed =
                          usedFields.has(f.key) && f.key !== currentField;
                        return (
                          <option
                            key={f.key}
                            value={f.key}
                            disabled={isUsed}
                          >
                            {f.label}
                            {isUsed ? " (used)" : ""}
                          </option>
                        );
                      })}
                    </select>
                    {fieldDef && fieldDef.inputType === "select" && (
                      <span
                        style={{
                          marginLeft: "6px",
                          fontSize: "10px",
                          textTransform: "uppercase",
                          fontWeight: 600,
                          color: "#8b5cf6",
                          background: "#ede9fe",
                          padding: "1px 6px",
                          borderRadius: "3px",
                        }}
                      >
                        dropdown
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Editable data preview — all rows */}
      {mappedEntries.length > 0 && (
        <div style={{ marginTop: "1rem" }}>
          <h3 style={{ fontSize: "15px", fontWeight: 600, marginBottom: "0.5rem" }}>
            Data preview &amp; edit ({editableRows.length} rows)
          </h3>
          <div
            style={{
              maxHeight: "40vh",
              overflowY: "auto",
              overflowX: "auto",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
            }}
          >
            <table
              style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}
            >
              <thead>
                <tr style={{ background: "#f1f5f9", position: "sticky", top: 0, zIndex: 5 }}>
                  <th style={{ ...previewThStyle, width: "36px" }}>#</th>
                  {mappedEntries.map((e) => (
                    <th key={e.dbField} style={previewThStyle}>
                      {dbFieldMap[e.dbField]?.label || e.dbField}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {editableRows.map((row, rIdx) => (
                  <tr key={rIdx} style={{ borderBottom: "1px solid #e5e7eb" }}>
                    <td style={{ ...previewTdStyle, textAlign: "center", color: "#94a3b8" }}>
                      {rIdx + 1}
                    </td>
                    {mappedEntries.map((e) => {
                      const val = Array.isArray(row) ? row[e.index] : row[e.index];
                      const fieldDef = dbFieldMap[e.dbField];
                      const isDropdown = fieldDef?.inputType === "select" && fieldDef?.options;
                      const rawVal = val !== null && val !== undefined ? String(val) : "";
                      const strVal = isDropdown ? matchDropdownValue(e.dbField, rawVal, fieldDef.options) : rawVal;
                      const isUnmatched =
                        isDropdown &&
                        strVal &&
                        !fieldDef.options.some(
                          (opt) => getOptionValue(opt) === strVal
                        );
                      return (
                        <td key={e.dbField} style={previewTdStyle}>
                          {isDropdown ? (
                            <select
                              value={strVal}
                              onChange={(ev) =>
                                handleEditCell(rIdx, e.index, ev.target.value)
                              }
                              style={{
                                width: "100%",
                                padding: "2px 4px",
                                border: `1px solid ${
                                  isUnmatched ? "#dc2626" : "#c4b5fd"
                                }`,
                                borderRadius: "4px",
                                fontSize: "13px",
                                background: isUnmatched ? "#fef2f2" : "#f5f3ff",
                                minWidth: "60px",
                              }}
                            >
                              <option value="">—</option>
                              {fieldDef.options.map((opt) => {
                                const optVal = getOptionValue(opt);
                                const optLabel = typeof opt === "object" ? opt.label : opt;
                                return (
                                  <option key={optVal} value={optVal}>
                                    {optLabel}
                                  </option>
                                );
                              })}
                              {isUnmatched && (
                                <option value={strVal}>{strVal} (Unmatched)</option>
                              )}
                            </select>
                          ) : (
                            <input
                              type="text"
                              value={strVal}
                              onChange={(ev) =>
                                handleEditCell(rIdx, e.index, ev.target.value)
                              }
                              style={{
                                width: "100%",
                                padding: "2px 4px",
                                border: "1px solid #e5e7eb",
                                borderRadius: "4px",
                                fontSize: "13px",
                                minWidth: "60px",
                              }}
                            />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div
        style={{
          marginTop: "1rem",
          display: "flex",
          gap: "0.5rem",
          alignItems: "center",
        }}
      >
        {hasUnmatchedDropdown && (
          <span style={{ fontSize: "12px", color: "#b91c1c", marginRight: "auto" }}>
            Some dropdown values are not matched to allowed options. Fix all red dropdowns
            before confirming the import.
          </span>
        )}
        <button onClick={onBack} className="btn btn-secondary">
          Back
        </button>
        <button
          onClick={handleConfirmClick}
          className="btn btn-primary"
          disabled={mappedCount === 0 || hasUnmatchedDropdown}
        >
          Confirm &amp; Import ({rowCount} rows)
        </button>
      </div>
    </div>
  );
}

const previewThStyle = {
  padding: "6px 8px",
  textAlign: "left",
  fontWeight: 600,
  fontSize: "11px",
  textTransform: "uppercase",
  letterSpacing: "0.03em",
  color: "#64748b",
  borderBottom: "2px solid #e5e7eb",
  whiteSpace: "nowrap",
};

const previewTdStyle = {
  padding: "3px 6px",
  verticalAlign: "middle",
};

const thStyle = {
  padding: "8px 10px",
  textAlign: "left",
  fontWeight: 600,
  fontSize: "11px",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "#64748b",
  borderBottom: "2px solid #e5e7eb",
  whiteSpace: "nowrap",
};

const tdStyle = {
  padding: "6px 10px",
  verticalAlign: "middle",
};

export default function ImportProperties({ communityId }) {
  const [file, setFile] = useState(null);
  const [step, setStep] = useState(1); // 1: file select, 2: mapping, 3: preview/edit, 4: results
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  // Mapping state
  const [excelHeaders, setExcelHeaders] = useState([]);
  const [rawDataRows, setRawDataRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [editing, setEditing] = useState([]);

  // Dynamic Dropdowns State
  const [communities, setCommunities] = useState([]);
  const [subdivisions, setSubdivisions] = useState([]);
  const [dynamicDBFields, setDynamicDBFields] = useState(DB_FIELDS);

  // Fetch Communities and Subdivisions on Mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [commRes, subRes] = await Promise.all([
          fetchAllCommunities(),
          fetchSubdivisions(),
        ]);
        setCommunities(commRes);
        setSubdivisions(subRes);
      } catch (err) {
        console.error("Failed to fetch communities or subdivisions:", err);
      }
    };
    fetchData();
  }, []);

  // Update dynamicDBFields when communities or subdivisions arrive
  useEffect(() => {
    const commOptions = communities.map(c => ({ value: c.name, label: c.name }));
    const commShortOptions = communities.map(c => ({ value: c.shortName || c.name, label: c.shortName || c.name }));
    const subOptions = subdivisions.map(s => ({ value: s.fullName || s.shortName, label: s.fullName || s.shortName }));
    const subShortOptions = subdivisions.map(s => ({ value: s.shortName || s.fullName, label: s.shortName || s.fullName }));

    const updatedDBFields = DB_FIELDS.map(field => {
      if (field.key === "community_name") {
        return { ...field, options: commOptions };
      }
      if (field.key === "community_short_name") {
        return { ...field, options: commShortOptions };
      }
      if (field.key === "subdivision_name") {
        return { ...field, options: subOptions };
      }
      if (field.key === "subdivision_short") {
        return { ...field, options: subShortOptions };
      }
      return field;
    });
    setDynamicDBFields(updatedDBFields);
  }, [communities, subdivisions]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
    setStep(1);
    setResults(null);
  };

  const handleParse = async () => {
    if (!file || !status) return;
    setLoading(true);
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];

      // Parse as array of arrays to detect two-row headers
      const rawData = XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
        defval: "",
      });
      const { headers, dataRows } = detectHeaders(rawData);

      // Detect date columns and convert serial numbers
      const dateHeaderIndices = [];
      headers.forEach((h, i) => {
        const upper = (h || "").toUpperCase();
        if (upper.includes("DATE")) {
          dateHeaderIndices.push(i);
        }
      });

      const processedRows = dataRows
        .filter((row) =>
          row.some(
            (cell) => cell !== "" && cell !== null && cell !== undefined
          )
        )
        .map((row) => {
          const newRow = [...row];
          dateHeaderIndices.forEach((idx) => {
            if (typeof newRow[idx] === "number") {
              newRow[idx] = excelDateToString(newRow[idx]);
            }
          });
          return newRow;
        });

      // Generate auto-mapping
      const autoMapping = guessMapping(headers);

      setExcelHeaders(headers);
      setRawDataRows(processedRows);
      setMapping(autoMapping);
      setStep(2); // Go to mapping step
    } catch (err) {
      alert("Parse failed: " + err.message);
    }
    setLoading(false);
  };

  const handleMappingChange = (colIndex, newField) => {
    setMapping((prev) => ({ ...prev, [colIndex]: newField }));
  };

  const handleMappingConfirm = (editedRows) => {
    // Apply the mapping to produce objects with DB field keys
    const mapped = editedRows.map((row) => {
      const obj = {};
      for (let idx = 0; idx < excelHeaders.length; idx++) {
        const dbField = mapping[idx];
        if (dbField) {
          const value = Array.isArray(row) ? row[idx] : row[idx];
          obj[dbField] =
            value !== undefined && value !== null ? String(value).trim() : "";
        }
      }
      return obj;
    });
    setEditing(mapped);
    setShowConfirm(true);
  };

  const handleConfirm = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.post("/api/properties/import/confirm", {
        properties: editing,
        communityId,
        status,
      });
      setResults(res.data.results || []);
      setStep(4);
      setShowConfirm(false);
    } catch (err) {
      alert("Import failed: " + (err.response?.data?.error || err.message));
    }
    setLoading(false);
  };

  return (
    <div className="p-4">
      {/* Step 1: Select file & status */}
      {step === 1 && (
        <>
          <div>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="mb-2 p-2 border"
            >
              <option value="">Select status</option>
              {propertyTypes.map((pt) => (
                <option key={pt.value} value={pt.value}>
                  {pt.label}
                </option>
              ))}
            </select>
            <input
              type="file"
              accept=".xls,.xlsx"
              onChange={handleFileChange}
              className="mb-2"
            />
            <button
              onClick={handleParse}
              disabled={loading || !file || !status}
              className="btn btn-primary"
            >
              Parse & Map Columns
            </button>
          </div>
        </>
      )}

      {/* Step 2: Column mapping + editable preview */}
      {step === 2 && (
        <ColumnMappingStep
          dynamicDBFields={dynamicDBFields}
          excelHeaders={excelHeaders}
          allRows={rawDataRows}
          mapping={mapping}
          onMappingChange={handleMappingChange}
          onConfirm={handleMappingConfirm}
          onBack={() => setStep(1)}
          rowCount={rawDataRows.length}
        />
      )}

      {/* Confirm dialog */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-lg">
            <h3 className="text-lg font-bold mb-2">Confirm import</h3>
            <p>Are you sure you want to import these properties?</p>
            <button onClick={handleConfirm} className="btn btn-success mt-2">
              Yes, Import
            </button>
            <button
              onClick={() => setShowConfirm(false)}
              className="btn btn-secondary ml-2"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Results */}
      {step === 4 && (
        <div>
          <h2 className="text-xl font-bold mb-2">Import results</h2>
          <ul>
            {results?.map((r, i) => (
              <li
                key={i}
                className={r.success ? (r.warning ? "text-amber-600" : "text-green-600") : "text-red-600"}
              >
                {r.success
                  ? `Imported property ${r.mls || `#${r.id}`}${r.warning ? ` - ${r.warning}` : ""}`
                  : `Error${r.mls ? ` (${r.mls})` : ""}: ${r.error}`}
              </li>
            ))}
          </ul>
          <button onClick={() => setStep(1)} className="btn btn-primary mt-4">
            Import More
          </button>
        </div>
      )}
    </div>
  );
}
